// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * A drawing scale that survives the window being resized.
 *
 * The scale used to be stored as feet per *screen pixel*, measured at whatever
 * size the canvas happened to be when the person calibrated it. Drag the
 * window wider and the plan image grows, the same fence now spans more screen
 * pixels, and every measurement on the drawing quietly becomes wrong — while
 * still reading as a confident number. For a tool whose whole job is saying
 * whether things fit, that is the worst kind of bug: silent and plausible.
 *
 * It is stored instead as feet per one percent of the drawing's width, which
 * is a property of the drawing rather than of the window. The plan image keeps
 * its own aspect ratio, so the overlay scales uniformly and a live
 * feet-per-pixel can always be derived from the canvas as it is right now.
 *
 * Positions on the drawing are percentages of width and height independently.
 * Because the image is never stretched, a percent of height covers a different
 * number of feet than a percent of width whenever the sheet is not square, so
 * distances are measured with the vertical leg corrected by the aspect ratio.
 */

export interface PlanScale {
  preset: string;
  /** Feet per 1% of the drawing's width. Null when nothing is calibrated. */
  feetPerPct?: number | null;
  /** Legacy: feet per screen pixel, from before the scale was stable. */
  feetPerPx?: number | null;
}

export interface Canvas { w: number; h: number }

/** A screen-pixel length in feet, for the canvas as it is right now. */
export function feetPerPixel(scale: PlanScale | null | undefined, canvas: Canvas): number | null {
  const perPct = scale?.feetPerPct;
  if (!perPct || !Number.isFinite(perPct) || perPct <= 0) return null;
  if (!canvas?.w || !Number.isFinite(canvas.w) || canvas.w <= 0) return null;
  return perPct / (canvas.w / 100);
}

/**
 * The distance between two points on the drawing, in width-percent units.
 *
 * The vertical leg is scaled by the canvas aspect so both legs are in the same
 * real-world unit. Measuring without this makes a diagonal on a wide sheet
 * read short, which is exactly the kind of error nobody catches by eye.
 */
export function pctDistance(
  a: { x: number; y: number },
  b: { x: number; y: number },
  canvas: Canvas,
): number {
  const aspect = canvas?.w ? (canvas.h || canvas.w) / canvas.w : 1;
  const dx = b.x - a.x;
  const dy = (b.y - a.y) * aspect;
  return Math.hypot(dx, dy);
}

/**
 * The scale implied by saying "this distance is N feet".
 *
 * This is the only way to get a scale that is actually true of the uploaded
 * drawing, which is why it is the one the UI pushes people toward.
 */
export function calibrate(
  a: { x: number; y: number },
  b: { x: number; y: number },
  feet: number,
  canvas: Canvas,
): number | null {
  const d = pctDistance(a, b, canvas);
  if (!d || !Number.isFinite(feet) || feet <= 0) return null;
  return feet / d;
}

/**
 * How wide the sheet really is, in inches, for a drawing at a named scale.
 *
 * The inverse of presetToFeetPerPct: if 1% of the width is F feet and the
 * drawing is at 1 inch to S feet, the sheet is F*100/S inches across. This is
 * the number that makes a printed scale true -- tell a PDF its page is that
 * wide and 100% on a printer is exactly 1" = S'.
 *
 * Only a named scale has an answer. A drawing calibrated against a known
 * distance has a real-world scale but no paper scale, because nothing in it
 * says how big the paper was meant to be.
 */
export function sheetInchesFor(
  feetPerPct: number | null | undefined,
  feetPerInch: number | null | undefined,
): number | null {
  if (!feetPerPct || !Number.isFinite(feetPerPct) || feetPerPct <= 0) return null;
  if (!feetPerInch || !Number.isFinite(feetPerInch) || feetPerInch <= 0) return null;
  return (feetPerPct * 100) / feetPerInch;
}

/**
 * A named scale resolved against the sheet it was drawn on.
 *
 * "1 inch = 20 feet" is a statement about paper, so it needs to know how much
 * paper the image is. Given the image's own pixel width and the dots per inch
 * it was scanned or exported at, the whole sheet is a known number of inches
 * across and therefore a known number of feet.
 *
 * Before this, a preset meant "one inch of *your screen* is twenty feet",
 * which made the drawing's scale depend on the reader's monitor.
 */
export function presetToFeetPerPct(
  feetPerInch: number | null | undefined,
  imageWidthPx: number,
  dpi = 96,
): number | null {
  if (!feetPerInch || !Number.isFinite(feetPerInch) || feetPerInch <= 0) return null;
  if (!imageWidthPx || !Number.isFinite(imageWidthPx) || imageWidthPx <= 0) return null;
  if (!dpi || dpi <= 0) return null;
  const sheetInches = imageWidthPx / dpi;
  return (sheetInches * feetPerInch) / 100;
}

/**
 * A scale read from a saved plan, converted forward if it predates this.
 *
 * A legacy scale can only be converted using the canvas it is being opened at,
 * because the size it was calibrated against was never recorded. That makes
 * the conversion exactly as accurate as the old value already was — no worse,
 * and from this point it stops drifting. Pretending to recover the original
 * would be inventing precision that was never there.
 */
export function normalizeScale(scale: PlanScale | null | undefined, canvas: Canvas): PlanScale {
  const base: PlanScale = { preset: scale?.preset || "none", feetPerPct: scale?.feetPerPct ?? null };
  if (base.feetPerPct) return base;

  const legacy = scale?.feetPerPx;
  if (legacy && Number.isFinite(legacy) && legacy > 0 && canvas?.w > 0) {
    return { preset: base.preset, feetPerPct: legacy * (canvas.w / 100) };
  }
  return base;
}

/** Whether a plan can measure anything at all. */
export function isCalibrated(scale: PlanScale | null | undefined): boolean {
  return !!scale?.feetPerPct && Number.isFinite(scale.feetPerPct) && scale.feetPerPct! > 0;
}

/** Feet as a builder writes them: 45', 10'-6", 8" below a foot. */
export function formatFeet(ft: number): string {
  if (!Number.isFinite(ft)) return "—";
  const total = Math.abs(ft);
  const whole = Math.floor(total + 1e-9);
  const inches = Math.round((total - whole) * 12);
  if (whole === 0) return `${inches || 0}"`;
  if (inches === 0 || inches === 12) return `${whole + (inches === 12 ? 1 : 0)}'`;
  return `${whole}'-${inches}"`;
}
